import _esef from "../functions/profiles/esef.js";
import _getFormatOptions from "../functions/helpers/getFormatOptions.js";
import _convertToPlainText from "../functions/quill/convertToPlainText.js";
import _convertToHtml from "../functions/quill/convertToHtml.js";
import _removeInvalidEvents from "../functions/eventGroups/removeInvalidEvents.js";
import _metadataHelper from "../functions/helpers/metadata.js";
import _Metadata from "../classes/metadata.js";
import _Event from "../classes/event.js";
const Event = _Event;
const Metadata = _Metadata;
const metadataHelper = _metadataHelper;
const removeInvalidEvents = _removeInvalidEvents;
const convertToHtml = _convertToHtml;
const convertToPlainText = _convertToPlainText;
const getFormatOptions = _getFormatOptions;
const esef = _esef;
function toBuffer(input) {
  if (Buffer.isBuffer(input)) return input;
  if (input instanceof Uint8Array) return Buffer.from(input);
  if (typeof input === "string") {
    // Hex string from binaryFormats path, or accidental utf8
    if (/^[0-9a-fA-F]+$/.test(input) && input.length % 2 === 0) {
      return Buffer.from(input, "hex");
    }
    return Buffer.from(input, "binary");
  }
  throw new Error("ESEF: unsupported input type");
}
export default {
  decode: function (input, options) {
    const buf = toBuffer(input);
    const parsed = esef.parseEsf(buf);
    const frameRate = options.frameRate || 25;
    const dropFrame = options.dropFrame || false;
    const events = parsed.cues.map(cue => {
      const ev = new Event();
      // Cue window → Event timing; fade/pan/audio windows are format-only and ignored
      ev.start = esef.packedTcToSec(cue.overallIn, frameRate, dropFrame);
      ev.end = esef.packedTcToSec(cue.overallOut, frameRate, dropFrame);
      ev.audioFile = cue.wav;
      ev.text = convertToHtml(cue.text.replace(/\r\n/g, "\n").replace(/\r/g, "\n"));
      return ev;
    });

    // Stash file-level info on a synthetic holder consumed in postProcess
    events._esefMeta = {
      title: parsed.title,
      authorship: parsed.authorship,
      fileIn: parsed.fileIn,
      fileOut: parsed.fileOut,
      headerRaw: parsed.headerRaw,
      groupPreamble: parsed.groupPreamble
    };
    return events;
  },
  encode: function (eventGroup, options) {
    const encodingOptions = getFormatOptions(options.formatOptions);
    const frameRate = options.frameRate || 25;
    const dropFrame = options.dropFrame || false;
    const fadeFrames = parseInt(encodingOptions["Fade Duration Frames"], 10);
    const fadeFrameCount = Number.isFinite(fadeFrames) && fadeFrames >= 0 ? fadeFrames : esef.DEFAULT_FADE_FRAMES;
    const titleMeta = (eventGroup.metadata || []).find(m => m.name === "title" || m.name === "ESEF Title");
    const title = encodingOptions["Title"] && encodingOptions["Title"] !== "" ? encodingOptions["Title"] : titleMeta && titleMeta.value || eventGroup.name || "Untitled";
    const cues = eventGroup.events.map((event, index) => {
      const windows = esef.deriveWindows(event.start, event.end, frameRate, dropFrame, fadeFrameCount);
      const plain = convertToPlainText(event.text || "").replace(/\n/g, "\r\n");
      return {
        ...windows,
        text: plain,
        wav: esef.ensureWavName(event.audioFile),
        trailerExtra: 0,
        index: index + 1
      };
    });
    const headerMeta = (eventGroup.metadata || []).find(m => m.name === "esefHeaderRaw");
    let headerRaw = null;
    if (headerMeta && headerMeta.value) {
      try {
        headerRaw = Buffer.from(headerMeta.value, "base64");
      } catch (_) {
        headerRaw = null;
      }
    }
    const preambleMeta = (eventGroup.metadata || []).find(m => m.name === "esefGroupPreamble");
    let groupPreamble = null;
    if (preambleMeta && preambleMeta.value) {
      try {
        groupPreamble = Buffer.from(preambleMeta.value, "base64");
      } catch (_) {
        groupPreamble = null;
      }
    }
    return esef.buildEsf({
      title,
      cues,
      fileIn: cues[0].overallIn,
      fileOut: cues[cues.length - 1].overallOut,
      headerRaw,
      groupPreamble
    }, {
      fullLastTrailer: encodingOptions["Full Last Trailer"] === "yes"
    });
  },
  preProcess: {
    encode: function (eventGroup) {
      return removeInvalidEvents(eventGroup);
    },
    decode: function (input) {
      return input;
    }
  },
  postProcess: {
    encode: function (output) {
      return output;
    },
    decode: function (eventGroup, options) {
      eventGroup.type = "audio description";

      // Recover file-level metadata stashed on the events array during decode
      const meta = eventGroup.events && eventGroup.events._esefMeta;
      if (meta) {
        if (meta.title) {
          eventGroup.name = meta.title;
          eventGroup.metadata.push(new Metadata({
            name: "ESEF Title",
            type: "text-input",
            value: meta.title
          }));
        }
        if (meta.authorship) {
          eventGroup.metadata.push(new Metadata({
            name: "ESEF Authorship",
            type: "text-input",
            value: meta.authorship
          }));
        }
        if (meta.headerRaw && meta.headerRaw.length) {
          eventGroup.metadata.push(new Metadata({
            name: "esefHeaderRaw",
            type: "text-input",
            value: meta.headerRaw.toString("base64")
          }));
        }
        if (meta.groupPreamble && meta.groupPreamble.length) {
          eventGroup.metadata.push(new Metadata({
            name: "esefGroupPreamble",
            type: "text-input",
            value: meta.groupPreamble.toString("base64")
          }));
        }
        /* Standard metadata keys so the title/authorship can be passed through to other formats */
        metadataHelper.setMetadata(eventGroup, "programTitle", meta.title, {
          source: "ESEF Title"
        });
        metadataHelper.setMetadata(eventGroup, "author", meta.authorship, {
          source: "ESEF Authorship"
        });
        delete eventGroup.events._esefMeta;
      }
      if (options && options.frameRate) {
        eventGroup.frameRate = options.frameRate;
      }
      eventGroup.dropFrame = false;
      return eventGroup;
    }
  }
};